import { useEffect, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Gate that bounces unauthenticated users to /login. The login page
 * triggers the Keycloak redirect; once Keycloak round-trips back to
 * `/auth/callback`, react-oidc-context loads the user and the gate
 * lets the original destination render.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Treat auth errors as "please sign in again" rather than leaving the
    // user staring at a spinner forever.
    if (auth.error) {
      console.warn('OIDC error, forcing logout:', auth.error);
    }
  }, [auth.error]);

  if (auth.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading session…
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
}
